import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';

interface SettingsForm {
  siteName: string;
  siteDescription: string;
  logoUrl: string;
  primaryColor: string;
  accentColor: string;
  darkMode: boolean;
  timezone: string;
  language: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  twitterUrl: string;
  instagramUrl: string;
  githubUrl: string;
  footerText: string;
  customCss: string;
  customJs: string;
}

const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Dubai',
  'Asia/Tehran',
];

export function SettingsPage() {
  const queryClient = useQueryClient();

  const [form, setForm] = useState<SettingsForm>({
    siteName: '',
    siteDescription: '',
    logoUrl: '',
    primaryColor: '#7c3aed',
    accentColor: '#f59e0b',
    darkMode: false,
    timezone: 'UTC',
    language: 'en',
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
    twitterUrl: '',
    instagramUrl: '',
    githubUrl: '',
    footerText: '',
    customCss: '',
    customJs: '',
  });

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const res = await api.settings.get();
      return res.data;
    },
  });

  useEffect(() => {
    if (settings) {
      setForm((prev) => ({
        ...prev,
        ...settings,
        darkMode: settings.darkMode === 'true',
      }));
    }
  }, [settings]);

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, string>) => api.settings.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data: Record<string, string> = {};
    Object.entries(form).forEach(([key, value]) => {
      data[key] = typeof value === 'boolean' ? String(value) : String(value ?? '');
    });
    updateMutation.mutate(data);
  };

  const updateField = <K extends keyof SettingsForm>(key: K, value: SettingsForm[K]) => {
    setForm({ ...form, [key]: value });
  };

  if (isLoading) {
    return (
      <div className="space-y-6 max-w-3xl mx-auto">
        <Skeleton className="h-10 w-48" />
        <div className="space-y-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-3xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-surface-900">Settings</h1>
        <p className="text-surface-500 mt-1">Configure your site settings</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card variant="glass" className="p-6 space-y-5">
          <h3 className="text-lg font-bold text-surface-900">General</h3>

          <Input
            label="Site Name"
            value={form.siteName}
            onChange={(e) => updateField('siteName', e.target.value)}
            placeholder="My Quote Platform"
          />

          <div>
            <label className="block text-sm font-medium text-surface-700 mb-2">
              Site Description
            </label>
            <textarea
              value={form.siteDescription}
              onChange={(e) => updateField('siteDescription', e.target.value)}
              rows={3}
              className="w-full rounded-2xl border border-surface-200 bg-white px-4 py-3 text-surface-900 shadow-sm transition-all duration-300 placeholder:text-surface-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 resize-none"
              placeholder="A beautiful quote of the day platform..."
            />
          </div>

          <Input
            label="Logo URL"
            value={form.logoUrl}
            onChange={(e) => updateField('logoUrl', e.target.value)}
            placeholder="https://example.com/logo.png"
          />
        </Card>

        <Card variant="glass" className="p-6 space-y-5">
          <h3 className="text-lg font-bold text-surface-900">Appearance</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">Primary Color</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={form.primaryColor}
                  onChange={(e) => updateField('primaryColor', e.target.value)}
                  className="w-12 h-12 rounded-xl border border-surface-200 cursor-pointer"
                />
                <Input
                  value={form.primaryColor}
                  onChange={(e) => updateField('primaryColor', e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">Accent Color</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={form.accentColor}
                  onChange={(e) => updateField('accentColor', e.target.value)}
                  className="w-12 h-12 rounded-xl border border-surface-200 cursor-pointer"
                />
                <Input
                  value={form.accentColor}
                  onChange={(e) => updateField('accentColor', e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>
          </div>

          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm font-medium text-surface-700">Dark Mode Default</span>
            <div className="relative">
              <input
                type="checkbox"
                checked={form.darkMode}
                onChange={(e) => updateField('darkMode', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-surface-200 rounded-full peer peer-checked:bg-primary-500 transition-colors" />
              <div className="absolute left-1 top-1 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
            </div>
          </label>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">Timezone</label>
              <select
                value={form.timezone}
                onChange={(e) => updateField('timezone', e.target.value)}
                className="w-full rounded-2xl border border-surface-200 bg-white px-4 py-3 text-surface-900 shadow-sm transition-all duration-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">Language</label>
              <select
                value={form.language}
                onChange={(e) => updateField('language', e.target.value)}
                className="w-full rounded-2xl border border-surface-200 bg-white px-4 py-3 text-surface-900 shadow-sm transition-all duration-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              >
                <option value="en">English</option>
                <option value="fa">Persian</option>
                <option value="ar">Arabic</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
              </select>
            </div>
          </div>
        </Card>

        <Card variant="glass" className="p-6 space-y-5">
          <h3 className="text-lg font-bold text-surface-900">SEO</h3>
          <Input
            label="SEO Title"
            value={form.seoTitle}
            onChange={(e) => updateField('seoTitle', e.target.value)}
            placeholder="Quote of the Day - Daily Inspiration"
          />
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-2">SEO Description</label>
            <textarea
              value={form.seoDescription}
              onChange={(e) => updateField('seoDescription', e.target.value)}
              rows={2}
              className="w-full rounded-2xl border border-surface-200 bg-white px-4 py-3 text-surface-900 shadow-sm transition-all duration-300 placeholder:text-surface-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 resize-none"
              placeholder="Discover inspiring quotes daily..."
            />
          </div>
          <Input
            label="SEO Keywords"
            value={form.seoKeywords}
            onChange={(e) => updateField('seoKeywords', e.target.value)}
            placeholder="quotes, inspiration, daily quotes, motivation"
          />
        </Card>

        <Card variant="glass" className="p-6 space-y-5">
          <h3 className="text-lg font-bold text-surface-900">Social Links</h3>
          <Input
            label="Twitter / X URL"
            value={form.twitterUrl}
            onChange={(e) => updateField('twitterUrl', e.target.value)}
            placeholder="https://x.com/yourhandle"
          />
          <Input
            label="Instagram URL"
            value={form.instagramUrl}
            onChange={(e) => updateField('instagramUrl', e.target.value)}
            placeholder="https://instagram.com/yourhandle"
          />
          <Input
            label="GitHub URL"
            value={form.githubUrl}
            onChange={(e) => updateField('githubUrl', e.target.value)}
            placeholder="https://github.com/yourhandle"
          />
        </Card>

        <Card variant="glass" className="p-6 space-y-5">
          <h3 className="text-lg font-bold text-surface-900">Footer</h3>
          <Input
            label="Footer Text"
            value={form.footerText}
            onChange={(e) => updateField('footerText', e.target.value)}
            placeholder="Powered by Quoto"
          />
        </Card>

        <Card variant="glass" className="p-6 space-y-5">
          <h3 className="text-lg font-bold text-surface-900">Custom Code</h3>
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-2">Custom CSS</label>
            <textarea
              value={form.customCss}
              onChange={(e) => updateField('customCss', e.target.value)}
              rows={5}
              className="w-full rounded-2xl border border-surface-200 bg-surface-900 px-4 py-3 text-green-400 font-mono text-sm shadow-sm transition-all duration-300 placeholder:text-surface-500 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 resize-none"
              placeholder=".custom-class { color: red; }"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-2">Custom JavaScript</label>
            <textarea
              value={form.customJs}
              onChange={(e) => updateField('customJs', e.target.value)}
              rows={5}
              className="w-full rounded-2xl border border-surface-200 bg-surface-900 px-4 py-3 text-green-400 font-mono text-sm shadow-sm transition-all duration-300 placeholder:text-surface-500 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 resize-none"
              placeholder="console.log('Hello');"
            />
          </div>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Save size={18} />
            )}
            {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </form>
    </div>
  );
}
